import { ZOOM_MULTIPLIER } from '@components/ArtBoard/Canvas/constants'
import {
  type ArrowGridPoint,
  type ID,
  selectBlockParent,
  selectBlocksByNode,
  selectNodeChildren,
  selectRootNodesBySection,
} from '@octopus-do/octopus-project-core'
import { thunkAddBlock } from '@store/migrationActions/thunkActions/thunkBlocksActions'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { selectTagVisibility } from '@store/reducers/tags/visibility'
import type React from 'react'
import type { Dispatch } from 'react'

import type { AppState } from '@/types'
import * as ActionTypes from '../../actions'

const initialState: CanvasState = {
  scale: 1,
  width: 0,
  arrow: {
    selectedArrowId: null,
    selectedArrowGridPointId: null,
    offset: {
      x: 0,
      y: 0,
    },
    hideArrows: false,
  },
  node: {
    selectedNodes: [],
    selectedToEditNode: null,
    selectedNodeLink: null,
  },
  block: {
    selectedInNode: null,
    selectedToEditBlockId: null,
    ids: [],
    autoCreatedBlockId: null,
    editTitleValue: '',
    lastAddedBlockId: null,
  },
  symbol: {
    selectedSymbol: null,
    selectedToEditSymbol: null,
  },
  stickyNote: {
    selectedStickyNote: null,
    selectedToEditStickyNote: null,
  },
  section: {
    selectedSectionId: null,
    selectedSectionEditingId: null,
  },
  externalLink: {
    selectedExternalLink: null,
    selectedToEditExternalLink: null,
    externalLinkOffset: {
      x: 0,
      y: 0,
    },
  },
  uploadingImage: {
    id: null,
    uploading: false,
  },
  tags: {
    selectedTags: [],
    dropdownIsShown: null,
  },
  tabs: {
    activeTab: null,
    renameInputIsActive: false,
    isCopied: null,
  },
  previewColor: null,
  colorPickerIsOpen: false,
  attachments: [],
  selectedThread: null,
}

// todo: have common selections store: selected: { id, type(?) }[]

export type CanvasState = {
  selectedThread: ID | null
  attachments: ID[]
  scale: number
  width: number
  arrow: {
    selectedArrowId: string | null
    selectedArrowGridPointId: string | null
    offset: {
      x: number
      y: number
    }
    hideArrows: boolean
  }
  node: {
    selectedNodes: string[]
    selectedToEditNode: string | null
    selectedNodeLink: string | null
  }
  block: {
    selectedInNode: string | null
    selectedToEditBlockId: string | null
    ids: string[]
    autoCreatedBlockId: string | null
    editTitleValue: string
    lastAddedBlockId: ID | null
  }
  symbol: {
    selectedSymbol: string | null
    selectedToEditSymbol: string | null
  }
  stickyNote: {
    selectedStickyNote: string | null
    selectedToEditStickyNote: string | null
  }
  section: {
    selectedSectionId: string | null
    selectedSectionEditingId: string | null
  }
  externalLink: {
    selectedExternalLink: string | null
    selectedToEditExternalLink: string | null
    externalLinkOffset: {
      x: number
      y: number
    }
  }
  uploadingImage: {
    id: string | null
    uploading: boolean
  }
  tags: {
    selectedTags: ID[]
    // @todo move to artboard
    dropdownIsShown: string | null
  }
  tabs: {
    activeTab: string | null
    renameInputIsActive: boolean
    isCopied: boolean | null
  }
  previewColor: Record<string, string> | null
  // @todo move to artboard
  colorPickerIsOpen: boolean
}

export enum CanvasActionTypes {
  SET_SELECTED_THREAD = 'SET_SELECTED_THREAD',
  CLEAR_SELECTED_THREAD = 'CLEAR_SELECTED_THREAD',
  SET_WIDTH = 'SET_WIDTH',
  SET_SCALE = 'SET_SCALE',
  SELECT_NODES = 'SELECT_NODES',
  SET_EDIT_NODE = 'SET_EDIT_NODE',
  CLEAR_SELECTED_NODES = 'CLEAR_SELECTED_NODES',
  ADD_SELECTED_NODE = 'ADD_SELECTED_NODE',
  ADD_SELECTED_NODES = 'ADD_SELECTED_NODES',
  SELECT_BLOCK = 'SELECT_BLOCK',
  SELECT_BLOCKS = 'SELECT_BLOCKS',
  ADD_SELECTED_BLOCKS = 'ADD_SELECTED_BLOCKS',
  CLEAR_SELECTED_BLOCKS = 'CLEAR_SELECTED_BLOCKS',
  SELECT_TO_EDIT_BLOCK = 'SELECT_TO_EDIT_BLOCK',
  EDIT_BLOCK_TITLE_VALUE = 'EDIT_BLOCK_TITLE_VALUE',
  SET_AUTO_CREATED_BLOCK = 'SET_AUTO_CREATED_BLOCK',
  SET_UPLOADING_IMAGE = 'SET_UPLOADING_IMAGE',
  RESET_EDIT_BLOCK = 'RESET_EDIT_BLOCK',
  SELECT_SYMBOL = 'SELECT_SYMBOL',
  SELECT_TO_EDIT_SYMBOL = 'SELECT_TO_EDIT_SYMBOL',
  RESET_EDIT_SYMBOL = 'RESET_EDIT_SYMBOL',
  CLEAR_SELECTED_SYMBOL = 'CLEAR_SELECTED_SYMBOL',
  SELECT_STICKY_NOTE = 'SELECT_STICKY_NOTE',
  SELECT_TO_EDIT_STICKY_NOTE = 'SELECT_TO_EDIT_STICKY_NOTE',
  RESET_EDIT_STICKY_NOTE = 'RESET_EDIT_STICKY_NOTE',
  CLEAR_SELECTED_STICKY_NOTE = 'CLEAR_SELECTED_STICKY_NOTE',
  SELECT_SECTION = 'SELECT_SECTION',
  SELECT_TO_EDIT_SECTION = 'SELECT_TO_EDIT_SECTION',
  CLEAR_SELECTED_SECTION = 'CLEAR_SELECTED_SECTION',
  SELECT_EXTERNAL_LINK = 'SELECT_EXTERNAL_LINK',
  MOVE_EXTERNAL_LINK = 'SET_OFFSET_EXTERNAL_LINK',
  SELECT_EXTERNAL_LINKS = 'SELECT_EXTERNAL_LINKS',
  SELECT_TO_EDIT_EXTERNAL_LINK = 'SELECT_TO_EDIT_EXTERNAL_LINK',
  CLEAR_SELECTED_EXTERNAL_LINK = 'CLEAR_SELECTED_EXTERNAL_LINK',
  DRAG_NODE = 'DRAG_NODE',
  ZOOM_IN = 'ZOOM_IN',
  ZOOM_OUT = 'ZOOM_OUT',
  CLEAR_ALL_SELECTION = 'CLEAR_ALL_SELECTION',

  // arrow
  SET_SELECTED_ARROW_ID = 'SET_SELECTED_ARROW_ID',
  RESET_SELECTED_ARROW_ID = 'RESET_SELECTED_ARROW_ID',
  UPDATE_ARROW_POINT_OFFSET = 'UPDATE_ARROW_POINT_OFFSET',
  SET_SELECTED_ARROW_POINT_ID = 'SET_SELECTED_ARROW_POINT_ID',
  RESET_SELECTED_ARROW_POINT_ID = 'RESET_SELECTED_ARROW_POINT_ID',

  // node link
  SET_SELECTED_NODE_LINK = 'SET_SELECTED_NODE_LINK',

  // tabs
  SET_ACTIVE_TAB = 'SET_ACTIVE_TAB',
  SET_ACTIVE_RENAME_INPUT = 'SET_ACTIVE_RENAME_INPUT',
  SET_TAB_IS_COPIED = 'SET_TAB_IS_COPIED',

  // color
  SET_PREVIEW_COLOR = 'SET_PREVIEW_COLOR',
  SET_OPEN_COLOR_PICKER = 'SET_OPEN_COLOR_PICKER',

  // block
  SET_LAST_ADDED_BLOCK_ID = 'SET_LAST_ADDED_BLOCK_ID',

  // attachments
  SELECT_ATTACHMENT = 'SELECT_ATTACHMENT',
  CLEAR_SELECTED_ATTACHMENTS = 'CLEAR_SELECTED_ATTACHMENTS',

  // tags
  ADD_FILTER_TAG = 'ADD_FILTER_TAG',
  REMOVE_FILTER_TAG = 'REMOVE_FILTER_TAG',
  SHOW_TAG_DROPDOWN = 'SHOW_TAG_DROPDOWN',

  // arrows
  HIDE_ARROWS = 'HIDE_ARROWS',
}

type CanvasActions =
  | {
      type: CanvasActionTypes.SET_WIDTH
      payload: {
        width: number
      }
    }
  | {
      type: CanvasActionTypes.SET_SCALE
      payload: {
        scale: number
      }
    }
  | {
      type: CanvasActionTypes.SELECT_NODES
      payload: string[]
    }
  | {
      type: CanvasActionTypes.ADD_SELECTED_NODE
      payload: string
    }
  | {
      type: CanvasActionTypes.DRAG_NODE
      payload: string | null
    }
  | {
      type: CanvasActionTypes.SELECT_BLOCK
      payload: string | null
    }
  | {
      type: CanvasActionTypes.SELECT_BLOCKS
      payload: string[]
    }
  | {
      type: CanvasActionTypes.SET_UPLOADING_IMAGE
      payload: {
        id: string | null
        uploading: boolean
      }
    }
  | {
      type: CanvasActionTypes.SET_EDIT_NODE
      payload: string[]
    }
  | {
      type: CanvasActionTypes.SELECT_SYMBOL
      payload: string | null
    }
  | {
      type: CanvasActionTypes.CLEAR_SELECTED_SYMBOL
    }
  | {
      type: CanvasActionTypes.SELECT_STICKY_NOTE
      payload: string | null
    }
  | {
      type: CanvasActionTypes.SELECT_TO_EDIT_STICKY_NOTE
      payload: string | null
    }
  | {
      type: CanvasActionTypes.CLEAR_SELECTED_STICKY_NOTE
    }
  | {
      type: CanvasActionTypes.SELECT_SECTION
      payload: string | null
    }
  | {
      type: CanvasActionTypes.SELECT_TO_EDIT_SECTION
      payload: string | null
    }
  | {
      type: CanvasActionTypes.CLEAR_SELECTED_SECTION
    }
  | {
      type: CanvasActionTypes.SELECT_EXTERNAL_LINK
      payload: string | null
    }
  | {
      type: CanvasActionTypes.SELECT_EXTERNAL_LINKS
      payload: string[]
    }
  | {
      type: CanvasActionTypes.MOVE_EXTERNAL_LINK
      payload: {
        x: number
        y: number
      }
    }
  | {
      type: CanvasActionTypes.SET_SELECTED_ARROW_POINT_ID
      payload: {
        pointId: string
        x: number
        y: number
      }
    }
  | {
      type: CanvasActionTypes.CLEAR_SELECTED_EXTERNAL_LINK
    }
  | {
      type: CanvasActionTypes.CLEAR_ALL_SELECTION
    }
  | {
      type: CanvasActionTypes.SET_ACTIVE_TAB
      payload: string | null
    }
  | {
      type: CanvasActionTypes.SET_ACTIVE_RENAME_INPUT
      payload: boolean
    }
  | { type: CanvasActionTypes.SET_TAB_IS_COPIED; payload: boolean | null }
  | {
      type: CanvasActionTypes.SET_PREVIEW_COLOR
      payload: Record<string, string> | null
    }
  | { type: CanvasActionTypes.SET_OPEN_COLOR_PICKER; payload: boolean }
  | { type: CanvasActionTypes.SELECT_ATTACHMENT; payload: ID }
  | { type: CanvasActionTypes.CLEAR_SELECTED_ATTACHMENTS; payload: null }
  | { type: CanvasActionTypes.SHOW_TAG_DROPDOWN; payload: ID }
  | { type: CanvasActionTypes.ADD_FILTER_TAG; payload: ID }
  | { type: CanvasActionTypes.REMOVE_FILTER_TAG; payload: ID }
  | { type: CanvasActionTypes.HIDE_ARROWS; payload: boolean }
  | { type: CanvasActionTypes.SET_SELECTED_THREAD; payload: ID }
  | { type: CanvasActionTypes.CLEAR_SELECTED_THREAD; payload: null }
  | { type: CanvasActionTypes.SET_LAST_ADDED_BLOCK_ID; payload: ID | null }

export default function canvas(state = initialState, action: any) {
  switch (action.type) {
    case CanvasActionTypes.CLEAR_SELECTED_THREAD: {
      return {
        ...state,
        selectedThread: null,
      }
    }
    case CanvasActionTypes.SET_SELECTED_THREAD: {
      return {
        ...state,
        selectedThread: action.payload,
      }
    }
    case CanvasActionTypes.SET_WIDTH: {
      const { width } = action.payload

      return {
        ...state,
        width,
      }
    }
    case CanvasActionTypes.SET_SCALE: {
      const { scale } = action.payload

      return {
        ...state,
        scale,
      }
    }

    case CanvasActionTypes.ZOOM_IN: {
      const newScale = Math.min(state.scale + ZOOM_MULTIPLIER, 2)

      if (newScale <= 2) {
        return {
          ...state,
          scale: newScale,
        }
      }

      return state
    }

    case CanvasActionTypes.ZOOM_OUT: {
      const newScale = Math.max(state.scale - ZOOM_MULTIPLIER, 0.1)

      if (newScale >= 0.1) {
        return {
          ...state,
          scale: newScale,
        }
      }

      return state
    }

    case CanvasActionTypes.SELECT_NODES: {
      return {
        ...state,
        block: initialState.block,
        symbol: initialState.symbol,
        externalLink: initialState.externalLink,
        arrow: {
          ...initialState.arrow,
          hideArrows: state.arrow.hideArrows,
        },
        stickyNote: initialState.stickyNote,
        section: initialState.section,
        node: {
          ...state.node,
          selectedNodes: action.payload,
          selectedToEditNode: null,
        },
      }
    }

    case CanvasActionTypes.SET_EDIT_NODE: {
      return {
        ...state,
        block: initialState.block,
        symbol: initialState.symbol,
        node: {
          ...state.node,
          selectedNode: action.payload,
          selectedNodes: [action.payload],
          selectedToEditNode: action.payload,
        },
      }
    }

    case CanvasActionTypes.ADD_SELECTED_NODE: {
      return {
        ...state,
        block: initialState.block,
        node: {
          ...state.node,
          selectedNodes: [...state.node.selectedNodes, action.payload],
          selectedToEditNode: null,
        },
      }
    }

    case CanvasActionTypes.ADD_SELECTED_NODES: {
      return {
        ...state,
        block: initialState.block,
        node: {
          ...state.node,
          selectedNodes: [...state.node.selectedNodes, ...action.payload],
          selectedToEditNode: null,
        },
      }
    }

    case CanvasActionTypes.SET_SELECTED_NODE_LINK: {
      return {
        ...state,
        node: {
          ...state.node,
          selectedNodeLink: action.payload,
        },
      }
    }

    case CanvasActionTypes.CLEAR_SELECTED_NODES: {
      return {
        ...state,
        node: initialState.node,
      }
    }

    case CanvasActionTypes.SELECT_BLOCK: {
      return {
        ...state,
        node: initialState.node,
        symbol: initialState.symbol,
        externalLink: initialState.externalLink,
        arrow: {
          ...initialState.arrow,
          hideArrows: state.arrow.hideArrows,
        },
        stickyNote: initialState.stickyNote,
        section: initialState.section,
        block: {
          ...state.block,
          autoCreatedBlockId: null,
          selectedBlock: action.payload,
          selectedToEditBlockId: null,
          ids: [action.payload],
        },
      }
    }

    case CanvasActionTypes.SELECT_TO_EDIT_BLOCK: {
      const { nodeId, blockId, isAutoCreated, value = '' } = action.payload
      const autoCreatedBlockId = isAutoCreated ? blockId : null

      return {
        ...state,
        node: initialState.node,
        symbol: initialState.symbol,
        externalLink: initialState.externalLink,
        arrow: initialState.arrow,
        stickyNote: initialState.stickyNote,
        section: initialState.section,
        block: {
          ...state.block,
          selectedInNode: nodeId,
          selectedBlock: blockId,
          selectedToEditBlockId: blockId,
          editTitleValue: value,
          autoCreatedBlockId,
          ids: [blockId],
        },
      }
    }

    case CanvasActionTypes.EDIT_BLOCK_TITLE_VALUE: {
      return {
        ...state,
        block: {
          ...state.block,
          editTitleValue: action.payload,
        },
      }
    }

    case CanvasActionTypes.RESET_EDIT_BLOCK: {
      return {
        ...state,
        node: initialState.node,
        block: {
          ...state.block,
          editTitleValue: '',
          selectedToEditBlockId: null,
        },
      }
    }

    case CanvasActionTypes.ADD_SELECTED_BLOCKS: {
      return {
        ...state,
        node: initialState.node,
        block: {
          ...state.block,
          autoCreatedBlockId: null,
          selectedBlock: action.payload,
          ids: [...state.block.ids, action.payload],
        },
      }
    }

    case CanvasActionTypes.CLEAR_SELECTED_BLOCKS: {
      return {
        ...state,
        block: initialState.block,
        symbol: initialState.symbol,
      }
    }

    case CanvasActionTypes.SET_UPLOADING_IMAGE: {
      return {
        ...state,
        uploadingImage: action.payload,
      }
    }

    case CanvasActionTypes.SELECT_SYMBOL: {
      return {
        ...state,
        node: initialState.node,
        externalLink: initialState.externalLink,
        arrow: initialState.arrow,
        stickyNote: initialState.stickyNote,
        section: initialState.section,
        symbol: {
          ...state.symbol,
          selectedSymbol: action.payload,
          selectedToEditSymbol: null,
        },
      }
    }

    case CanvasActionTypes.SELECT_TO_EDIT_SYMBOL: {
      return {
        ...state,
        node: initialState.node,
        block: initialState.block,
        symbol: {
          ...state.symbol,
          selectedSymbol: action.payload,
          selectedToEditSymbol: action.payload,
        },
      }
    }

    case CanvasActionTypes.RESET_EDIT_SYMBOL: {
      return {
        ...state,
        symbol: {
          ...state.symbol,
          selectedToEditSymbol: null,
        },
      }
    }

    case CanvasActionTypes.CLEAR_SELECTED_SYMBOL: {
      return {
        ...state,
        symbol: initialState.symbol,
      }
    }

    case CanvasActionTypes.SELECT_EXTERNAL_LINK: {
      return {
        ...state,
        externalLink: {
          ...state.externalLink,
          selectedExternalLink: action.payload,
          selectedToEditExternalLink: null,
        },
      }
    }

    case CanvasActionTypes.CLEAR_SELECTED_EXTERNAL_LINK: {
      return {
        ...state,
        externalLink: initialState.externalLink,
      }
    }

    case CanvasActionTypes.MOVE_EXTERNAL_LINK: {
      return {
        ...state,
        externalLink: {
          ...state.externalLink,
          externalLinkOffset: action.payload,
        },
      }
    }

    case CanvasActionTypes.SELECT_STICKY_NOTE: {
      return {
        ...state,
        stickyNote: {
          ...state.stickyNote,
          selectedStickyNote: action.payload,
          selectedToEditStickyNote: null,
        },
      }
    }

    case CanvasActionTypes.SELECT_TO_EDIT_STICKY_NOTE: {
      return {
        ...state,
        stickyNote: {
          ...state.stickyNote,
          selectedStickyNote: action.payload,
          selectedToEditStickyNote: action.payload,
        },
      }
    }

    case CanvasActionTypes.CLEAR_SELECTED_STICKY_NOTE: {
      return {
        ...state,
        stickyNote: initialState.stickyNote,
      }
    }

    case CanvasActionTypes.SELECT_SECTION: {
      return {
        ...state,
        node: initialState.node,
        symbol: initialState.symbol,
        externalLink: initialState.externalLink,
        arrow: initialState.arrow,
        stickyNote: initialState.stickyNote,
        section: {
          selectedSectionId: action.payload,
          selectedSectionEditingId: null,
        },
      }
    }

    case CanvasActionTypes.SELECT_TO_EDIT_SECTION: {
      return {
        ...state,
        section: {
          selectedSectionId: action.payload,
          selectedSectionEditingId: action.payload,
        },
      }
    }

    case CanvasActionTypes.CLEAR_SELECTED_SECTION: {
      return {
        ...state,
        section: initialState.section,
      }
    }

    case CanvasActionTypes.DRAG_NODE: {
      return {
        ...state,
        node: {
          ...state.node,
          draggingNode: action.payload,
        },
      }
    }

    case ActionTypes.OPEN_BLOCK:
    case ActionTypes.OPEN_SYMBOL:
    case CanvasActionTypes.CLEAR_ALL_SELECTION: {
      return {
        ...state,
        node: initialState.node,
        block: initialState.block,
        symbol: initialState.symbol,
        stickyNote: initialState.stickyNote,
        externalLink: initialState.externalLink,
        arrow: initialState.arrow,
      }
    }

    case CanvasActionTypes.SET_SELECTED_ARROW_ID: {
      return {
        ...state,
        node: initialState.node,
        block: initialState.block,
        symbol: initialState.symbol,
        stickyNote: initialState.stickyNote,
        externalLink: initialState.externalLink,
        section: initialState.section,
        arrow: {
          ...state.arrow,
          selectedArrowId: action.payload.id,
        },
      }
    }

    case CanvasActionTypes.RESET_SELECTED_ARROW_ID: {
      return {
        ...state,
        arrow: {
          ...state.arrow,
          selectedArrowId: null,
          selectedArrowGridPointId: null,
        },
      }
    }
    case CanvasActionTypes.SET_SELECTED_ARROW_POINT_ID: {
      return {
        ...state,
        arrow: {
          ...state.arrow,
          selectedArrowGridPointId: action.payload.pointId,
          offset: {
            x: action.payload.x,
            y: action.payload.y,
          },
        },
      }
    }
    case CanvasActionTypes.RESET_SELECTED_ARROW_POINT_ID: {
      return {
        ...state,
        arrow: {
          ...state.arrow,
          selectedArrowGridPointId: null,
        },
      }
    }

    case CanvasActionTypes.UPDATE_ARROW_POINT_OFFSET: {
      const { arrowId, pointId, offset } = action.payload
      return {
        ...state,
        arrow: {
          ...state.arrow,
          selectedArrowId: arrowId,
          selectedArrowGridPointId: pointId,
          offset,
        },
      }
    }

    case CanvasActionTypes.SET_ACTIVE_TAB: {
      return {
        ...state,
        tabs: {
          ...state.tabs,
          activeTab: action.payload,
        },
      }
    }
    case CanvasActionTypes.SET_ACTIVE_RENAME_INPUT: {
      return {
        ...state,
        tabs: {
          ...state.tabs,
          renameInputIsActive: action.payload,
        },
      }
    }
    case CanvasActionTypes.SET_TAB_IS_COPIED: {
      return {
        ...state,
        tabs: {
          ...state.tabs,
          isCopied: action.payload,
        },
      }
    }
    case CanvasActionTypes.SET_PREVIEW_COLOR: {
      const previewColor =
        action.payload !== null
          ? {
              ...state.previewColor,
              ...action.payload,
            }
          : null
      return {
        ...state,
        previewColor,
      }
    }
    case CanvasActionTypes.SET_OPEN_COLOR_PICKER: {
      return {
        ...state,
        colorPickerIsOpen: action.payload,
      }
    }
    case CanvasActionTypes.SELECT_ATTACHMENT: {
      return {
        ...state,
        attachments: [action.payload],
      }
    }
    case CanvasActionTypes.CLEAR_SELECTED_ATTACHMENTS: {
      return {
        ...state,
        attachments: [],
      }
    }
    case CanvasActionTypes.SHOW_TAG_DROPDOWN: {
      return {
        ...state,
        tags: {
          ...state.tags,
          dropdownIsShown: action.payload,
        },
      }
    }
    case CanvasActionTypes.ADD_FILTER_TAG: {
      return {
        ...state,
        tags: {
          ...state.tags,
          selectedTags: [...state.tags.selectedTags, action.payload],
        },
      }
    }
    case CanvasActionTypes.REMOVE_FILTER_TAG: {
      return {
        ...state,
        tags: {
          ...state.tags,
          selectedTags: state.tags.selectedTags.filter(
            (tag) => tag !== action.payload
          ),
        },
      }
    }
    case CanvasActionTypes.HIDE_ARROWS: {
      return {
        ...state,
        arrow: {
          ...state.arrow,
          hideArrows: action.payload,
        },
      }
    }
    case CanvasActionTypes.SET_LAST_ADDED_BLOCK_ID: {
      return {
        ...state,
        block: {
          ...state.block,
          lastAddedBlockId: action.payload,
        },
      }
    }
    default:
      return state
  }
}

export const selectSymbol =
  // @ts-expect-error
  (e: React.MouseEvent, id: string) => (dispatch, getState) => {
    const { canvas } = getState()

    if (canvas.symbol.selectedSymbol !== null) {
      dispatch({
        type: CanvasActionTypes.SELECT_TO_EDIT_SYMBOL,
        payload: id,
      })
    } else {
      dispatch({
        type: CanvasActionTypes.SELECT_SYMBOL,
        payload: id,
      })
    }
  }

export const selectNode =
  (e: React.MouseEvent, id: string) =>
  (dispatch: Dispatch<any>, getState: () => AppState) => {
    const { canvas } = getState()

    if (e.shiftKey) {
      dispatch({
        type: CanvasActionTypes.ADD_SELECTED_NODE,
        payload: id,
      })

      return
    }

    if (canvas.node.selectedNodes.includes(id)) {
      dispatch(selectToEditNode(id))
    } else {
      dispatch({
        type: CanvasActionTypes.SELECT_NODES,
        payload: [id],
      })
    }
  }

export const selectSelectedThread = (state: AppState) =>
  state.canvas.selectedThread

const selectToEditNode =
  (id: string) => (dispatch: Dispatch<any>, getState: () => AppState) => {
    // @todo implement selector
    const state = getState()
    const tree = Object.fromEntries(
      state.manager.project.nodes.map((item) => [item.id, item])
    )

    if (tree[id]) {
      dispatch({
        type: CanvasActionTypes.SET_EDIT_NODE,
        payload: id,
      })
    }
  }

export const selectBlock =
  // @ts-expect-error
  (e: React.MouseEvent, id: string) => (dispatch, getState) => {
    const { canvas } = getState()

    // @todo translate
    // Если мы выбираем несколько блоков с помощью зажатого шифта, мы должны
    // убрать выбранные символы, так как нам нужно подсвечивать только выбранные блоки
    if (e.shiftKey) {
      dispatch({
        type: CanvasActionTypes.ADD_SELECTED_BLOCKS,
        payload: id,
      })
      dispatch({
        type: CanvasActionTypes.CLEAR_SELECTED_SYMBOL,
      })

      return
    }

    // we need React.MouseEvent to implement multiple select later
    // const { isSymbol, symbolId } = blocks[id]

    const block = migrationProjectSelector.getProjectBlockOrSymbolData(id)(
      getState()
    )

    const isSymbol = block.type === 'symbol'

    if (canvas.block.ids.includes(id)) {
      dispatch(selectToEditBlock(id))
    } else {
      dispatch({
        type: CanvasActionTypes.SELECT_BLOCK,
        payload: id,
      })
      // in case of multiple select later
      // dispatch({
      //   type: CanvasActionTypes.ADD_SELECTED_BLOCKS,
      //   payload: id,
      // })
    }

    if (isSymbol) {
      dispatch({
        type: CanvasActionTypes.SELECT_SYMBOL,
        payload: block.symbolId,
      })
    }
  }

// @ts-expect-error
export const selectToEditBlock = (id: string) => (dispatch, getState) => {
  const block = migrationProjectSelector.getProjectBlockOrSymbolData(id)(
    getState()
  )
  const nodeId = selectBlockParent(id)(getState())[0]

  if (!block) {
    return
  }

  const { title } = block

  dispatch({
    type: CanvasActionTypes.SELECT_TO_EDIT_BLOCK,
    payload: {
      nodeId,
      blockId: id,
      value: title,
    },
  })
}

// @TODO refactor
// @ts-expect-error
export const selectNextBlock = () => (dispatch, getState) => {
  const { canvas } = getState()

  const selectedBlock = canvas.block.ids[canvas.block.ids.length - 1]

  if (!selectedBlock) {
    return
  }

  const activeTab = migrationProjectSelector.getActiveOrFirstTabId(getState())

  const sections = migrationProjectSelector.getProjectSections(getState())

  const filteredSections = sections
    .filter((section) => section.tabId === activeTab && !section.collapsed)
    .sort((a, b) => a.order - b.order)
    .map((section) => section.id)

  // @todo review this
  // moved from old realisation of nextBlock in blocks.js
  // https://linear.app/scada/issue/OCT-1737
  // Page-tag filter (OCT-5007): pages filtered off the artboard are skipped,
  // and a collapsed page holding a match is walked into, matching what the
  // tree actually renders.
  const { filterIsActive, visibleNodeIds, forceExpandedIds } =
    selectTagVisibility(getState())

  const collectBlocksByNodes = (nodeIds: string[], acc: string[]) => {
    nodeIds.forEach((nodeId) => {
      if (filterIsActive && !visibleNodeIds.has(nodeId)) {
        return
      }

      const node = migrationProjectSelector.getProjectNodeById(nodeId)(
        getState()
      )

      const blocks = selectBlocksByNode(nodeId)(getState())

      if (blocks.length > 0) {
        acc.push(...blocks)
      }

      const children = selectNodeChildren(nodeId)(getState())

      const collapsed = node.collapsed && !forceExpandedIds.has(nodeId)

      if (!collapsed && children.length > 0) {
        collectBlocksByNodes(children, acc)
      }
    })

    return acc
  }

  const blockIds = filteredSections.reduce((accBlockIds, sectionId) => {
    const nodes = selectRootNodesBySection(sectionId)(getState())
    const ids = collectBlocksByNodes(nodes, [])
    if (ids.length > 0) {
      accBlockIds.push(...ids)
    }

    return accBlockIds
  }, [] as string[])

  const nextBlock = blockIds[blockIds.indexOf(selectedBlock) + 1]

  if (nextBlock) {
    dispatch({
      type: CanvasActionTypes.SELECT_BLOCK,
      payload: nextBlock,
    })
  } else {
    dispatch({ type: CanvasActionTypes.CLEAR_SELECTED_BLOCKS })
  }
}

export const createAutoCreatedBlock =
  (nodeId: string, blockId: string) =>
  // @ts-expect-error
  (dispatch, getState) => {
    const { artboard } = getState()

    // @todo review this
    if (artboard.mode !== 'symbol_library') {
      const blocksByNode = selectBlocksByNode(nodeId)(getState())

      const index = blocksByNode.indexOf(blockId) + 1

      const { colorId } = migrationProjectSelector.getProjectBlockOrSymbolData(
        blockId
      )(getState())

      const nextId = dispatch(thunkAddBlock(nodeId, colorId, index))

      dispatch({
        type: CanvasActionTypes.SELECT_TO_EDIT_BLOCK,
        payload: {
          nodeId,
          blockId: nextId,
          isAutoCreated: true,
        },
      })
    } else {
      dispatch({
        type: CanvasActionTypes.CLEAR_SELECTED_BLOCKS,
      })
    }
  }

export const setUploadingImage =
  (uploading: boolean) =>
  (dispatch: Dispatch<CanvasActions>, getState: () => AppState) => {
    const { canvas } = getState()
    const selectedBlock = canvas.block.ids[canvas.block.ids.length - 1]

    dispatch({
      type: CanvasActionTypes.SET_UPLOADING_IMAGE,
      payload: {
        id: selectedBlock,
        uploading,
      },
    })
  }

export const setUploadingImageSymbol =
  (uploading: boolean) =>
  (dispatch: Dispatch<CanvasActions>, getState: () => AppState) => {
    const { canvas } = getState()
    const { selectedSymbol } = canvas.symbol

    dispatch({
      type: CanvasActionTypes.SET_UPLOADING_IMAGE,
      payload: {
        id: selectedSymbol,
        uploading,
      },
    })
  }

export const setSelectedArrowPointId =
  (pointId: string) =>
  (dispatch: Dispatch<CanvasActions>, getState: () => AppState) => {
    const {
      manager: {
        project: { arrows },
      },
    } = getState()

    const points = arrows.reduce(
      (acc, arrow) => [...acc, ...arrow.points],
      [] as ArrowGridPoint[]
    )

    const point = points.find((p) => p.id === pointId)

    if (!point) {
      return
    }

    dispatch({
      type: CanvasActionTypes.SET_SELECTED_ARROW_POINT_ID,
      payload: {
        pointId,
        x: point.offset.x,
        y: point.offset.y,
      },
    })
  }

export const setSelectedNodeLink = (payload: string | null) => ({
  type: CanvasActionTypes.SET_SELECTED_NODE_LINK,
  payload,
})

export const setActiveTab = (payload: string | null) => {
  setTimeout(() => {
    // @TODO implament with redux-saga. We need to call custom event after setting active tab for pdf export
    // @ts-expect-error
    window.readyForPrint = true
  }, 1000)

  return {
    type: CanvasActionTypes.SET_ACTIVE_TAB,
    payload,
  }
}

export const setActiveRenameInput = (payload: boolean) => ({
  type: CanvasActionTypes.SET_ACTIVE_RENAME_INPUT,
  payload,
})

export const setTabIsCopied =
  (payload: string) =>
  async (dispatch: Dispatch<CanvasActions>, getState: () => AppState) => {
    // @todo implement selectors
    const {
      manager: {
        project: { tabs },
        meta: { id: projectId },
      },
    } = getState()
    let isCopied = false
    const id = tabs.length === 1 ? tabs[0].id : payload

    if (!navigator.clipboard) {
      // Old browser fallback
      document.execCommand('copy')
      isCopied = true

      return
    }

    try {
      await navigator.clipboard.writeText(
        `${window.location.origin}/${projectId}/tab/${id}`
      )

      isCopied = true
    } catch {
      isCopied = false
    }

    dispatch({
      type: CanvasActionTypes.SET_TAB_IS_COPIED,
      payload: isCopied,
    })
  }

export const setPreviewColor =
  (payload: string | null, id?: string) =>
  (dispatch: Dispatch<CanvasActions>, getState: () => AppState) => {
    const { canvas } = getState()
    const { node, block, externalLink, symbol, section } = canvas
    let payloadData = {}

    if (payload === null) {
      dispatch({
        type: CanvasActionTypes.SET_PREVIEW_COLOR,
        payload,
      })

      return
    }

    if (node.selectedNodes.length > 0 && !id) {
      payloadData = node.selectedNodes.reduce(
        (acc, cur) => {
          return { ...acc, [cur]: payload }
        },
        {} as Record<string, string>
      )
    }

    if (block.ids.length > 0) {
      payloadData = block.ids.reduce(
        (acc, cur) => {
          return { ...acc, [cur]: payload }
        },
        {} as Record<string, string>
      )
    }

    if (externalLink.selectedExternalLink) {
      payloadData = { [externalLink.selectedExternalLink]: payload }
    }

    if (symbol.selectedSymbol) {
      payloadData = { [symbol.selectedSymbol]: payload }
    }

    if (section.selectedSectionId) {
      payloadData = { [section.selectedSectionId]: payload }
    }

    if (id) {
      payloadData = { [id]: payload }
    }

    dispatch({
      type: CanvasActionTypes.SET_PREVIEW_COLOR,
      payload: payloadData,
    })
  }

export const setOpenPreviewColorPicker = (payload: boolean) => ({
  type: CanvasActionTypes.SET_OPEN_COLOR_PICKER,
  payload,
})

export const selectAttachment = (id: ID) => ({
  type: CanvasActionTypes.SELECT_ATTACHMENT,
  payload: id,
})

export const clearAttachmentsSelection = () => ({
  type: CanvasActionTypes.CLEAR_SELECTED_ATTACHMENTS,
  payload: null,
})

export const setShowTagDropdown = (payload: string | null) => ({
  type: CanvasActionTypes.SHOW_TAG_DROPDOWN,
  payload,
})

export const filterByTag = (id: ID) => ({
  type: CanvasActionTypes.ADD_FILTER_TAG,
  payload: id,
})

export const removeFilterByTag = (id: ID) => ({
  type: CanvasActionTypes.REMOVE_FILTER_TAG,
  payload: id,
})

export const setHideArrows = (payload: boolean) => ({
  type: CanvasActionTypes.HIDE_ARROWS,
  payload,
})

export const selectThread = (id: ID) => ({
  type: CanvasActionTypes.SET_SELECTED_THREAD,
  payload: id,
})

export const clearSelectedThread = () => ({
  type: CanvasActionTypes.CLEAR_SELECTED_THREAD,
  payload: null,
})

export const setLastAddedBlockId = (payload: ID | null) => ({
  type: CanvasActionTypes.SET_LAST_ADDED_BLOCK_ID,
  payload,
})
